import {
  CommentOutlined,
  BookOutlined,
  ControlOutlined,
  DatabaseOutlined,
  FilePdfOutlined,
  HistoryOutlined,
  FileTextOutlined,
  HomeOutlined,
  MenuOutlined,
  NotificationOutlined,
  ReadOutlined,
  SnippetsOutlined,
  RobotOutlined,
  ShoppingOutlined,
  TagsOutlined,
  TeamOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import {
  Button,
  Drawer,
  Flex,
  Grid,
  Layout,
  Menu,
  theme,
  Typography,
} from "antd";
import { Outlet, useLocation, useNavigate } from "react-router";
import { CabecalhoDaBarra, PerfilDaBarra, RodapeDaBarra, useBarraRecolhida } from "@titularis/ui";
import { signOut, useSession } from "../auth";

/**
 * Layout da administração. A densidade importa mais que no app do aluno - o
 * trabalho de cadastro e revisão é de tela grande -, mas **não ao ponto de
 * quebrar no celular**: com a barra recolhida ocupando 72px de uma tela de
 * 390px, um quinto da largura ia embora para uma fila de ícones e o conteúdo
 * espremia. Abaixo de `md` a barra vira gaveta, como no app do aluno, e a tela
 * inteira é do conteúdo.
 *
 * A barra lateral usa a mesma superfície do cabeçalho e dos cartões
 * (`colorBgContainer`), separada por uma borda: o backoffice fica com uma
 * superfície clara contínua e o conteúdo é que ganha destaque. Como a cor vem
 * de token, o modo escuro continua funcionando sem nenhum caso especial.
 *
 * Os itens do menu ficam desabilitados enquanto as telas não existem, para o
 * esqueleto de navegação já refletir o escopo.
 */

/**
 * O menu, numa lista só.
 *
 * **Fora do componente porque ele é também a tabela de seções.** A lista de
 * rotas conhecidas vivia escrita de novo dentro do `secaoAtual`, e as duas
 * cópias divergiram exatamente como cópia diverge: Comentário Assistido, Acervo
 * e Trilha nasceram no menu e nunca entraram na outra lista. O efeito era mudo -
 * nada quebrava, o item clicado simplesmente não acendia e "Progresso" ficava
 * marcado no lugar dele, porque a busca caía no destino final. Derivada da
 * mesma fonte, a divergência deixa de ser possível (G-CANONICAL).
 */
const ITENS = [
  { key: "/", icon: <HomeOutlined />, label: "Progresso" },
  { key: "/provas", icon: <FileTextOutlined />, label: "Provas" },
  { key: "/questoes", icon: <BookOutlined />, label: "Questões" },
  { key: "/reportes", icon: <WarningOutlined />, label: "Erros reportados" },
  {
    key: "/cadastro-assistido",
    icon: <RobotOutlined />,
    label: "Cadastro Assistido",
  },
  {
    key: "/comentario-assistido",
    icon: <CommentOutlined />,
    label: "Comentário Assistido",
  },
  { key: "/legislacao", icon: <ReadOutlined />, label: "Legislação Comentada" },
  // Junto do conteudo, e nao junto de Acesso: noticia e material que se
  // escreve, e quem a escreve e quem escreve comentario.
  { key: "/noticias", icon: <NotificationOutlined />, label: "Notícias" },
  {
    key: "/revista",
    icon: <SnippetsOutlined />,
    label: "Revista",
  },
  // Junto da Revista: os dois são PDF que a casa publica e mede.
  { key: "/materiais", icon: <FilePdfOutlined />, label: "Materiais grátis" },
  { key: "/acervo", icon: <DatabaseOutlined />, label: "Acervo" },
  { key: "/cadastros", icon: <TagsOutlined />, label: "Cadastros" },
  { key: "/acesso", icon: <ControlOutlined />, label: "Acesso" },
  // Antes de Pessoas: um diz o que se vende, o outro diz quem comprou.
  { key: "/produtos", icon: <ShoppingOutlined />, label: "Produtos" },
  /*
   * Pessoas, onde havia tres itens.
   *
   * "Assinantes", "Origem" e "Administradores" sairam do menu em 18/09/2026 e
   * viraram abas de uma secao so. Os tres liam a mesma populacao por eixos
   * diferentes - direito, canal e papel -, cada um com a propria busca e a
   * propria tabela, e nenhum deles sabia abrir **uma** pessoa. Quem procurava
   * alguem tinha de adivinhar em qual dos tres ela estaria. "Listas de espera"
   * seguiu o mesmo caminho em 25/09/2026: e o degrau de cima do funil de
   * Pessoas, e morava longe dele com outra regua.
   *
   * Fica ao lado de Acesso de proposito: um define o limite do gratuito, o
   * outro mostra quem bateu nele.
   */
  { key: "/pessoas", icon: <TeamOutlined />, label: "Pessoas" },
  { key: "/trilha", icon: <HistoryOutlined />, label: "Trilha" },
];

/**
 * As telas que só o Dono abre.
 *
 * Produtos nasceu com o portao proprio (`billing:manage`). Pessoas herdou o
 * motivo das tres telas que a formaram - listar conta exige `user:list`, ler
 * receita por canal exige `billing:manage`, e as duas so existem no papel
 * `owner` -, e ganhou um motivo mais forte que qualquer um deles: ela le **dado
 * pessoal de aluno**, e nao so o que ele comprou.
 *
 * Quem decide continua sendo a API (G-POLICY): esconder o item nao autoriza
 * nada, so evita oferecer o que vai falhar.
 */
const SO_DO_DONO = new Set(["/produtos", "/pessoas"]);

/**
 * O item do menu segue destacado dentro da seção: editar uma prova mantém
 * "Provas" marcado, em vez de a barra lateral parecer perdida.
 */
function secaoAtual(caminho: string): string {
  const secao = ITENS.map((item) => item.key)
    /*
     * A raiz fica de fora da procura e serve só de destino final. Dentro dela,
     * `startsWith("/")` casaria com qualquer caminho e o Progresso venceria
     * todas as telas - o mesmo sintoma que este arquivo acabou de perder, por
     * outro caminho.
     */
    .filter((rota) => rota !== "/")
    // Exato ou seguido de barra, nunca prefixo solto: sem a barra,
    // "/cadastros" acenderia dentro de "/cadastro-assistido".
    .find((rota) => caminho === rota || caminho.startsWith(`${rota}/`));

  return secao ?? "/";
}

/** Chave da preferência de recolhimento desta barra. */
const CHAVE_RECOLHIDA = "titularis:admin:barra-recolhida";

const LARGURA = 224;
const LARGURA_RECOLHIDA = 72;

export function AdminLayout() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: sessao } = useSession();
  const telas = Grid.useBreakpoint();
  const { recolhida, escolher, recolherPorTela } =
    useBarraRecolhida(CHAVE_RECOLHIDA);
  const [gavetaAberta, setGavetaAberta] = useState(false);

  const sair = async () => {
    await signOut();
    void navigate("/entrar", { replace: true });
  };

  const ehDono = (sessao?.user as { role?: string } | undefined)?.role === "owner";

  /*
   * A lista visível sai da mesma tabela que o `secaoAtual` usa: o filtro é de
   * exibição, e a rota destacada continua sendo encontrada por quem chegar nela
   * por link.
   */
  const itensVisiveis = ITENS.filter(
    (item) => ehDono || !SO_DO_DONO.has(item.key),
  );

  // O mesmo menu serve à barra e à gaveta: duplicá-lo faria os dois divergirem
  // no primeiro item novo.
  const menu = (compacta: boolean) => (
    <Menu
      mode="inline"
      inlineCollapsed={compacta}
      // Sem a borda própria do Menu: quem separa a barra do conteúdo é a
      // borda do Sider, e duas linhas coladas ficariam pesadas.
      style={{ borderInlineEnd: "none", flex: 1, overflowY: "auto" }}
      selectedKeys={[secaoAtual(location.pathname)]}
      onClick={({ key }) => {
        setGavetaAberta(false);
        void navigate(key);
      }}
      items={itensVisiveis}
    />
  );

  const perfil = (compacta: boolean) => (
    <RodapeDaBarra>
      <PerfilDaBarra
        nome={sessao?.user.name}
        email={sessao?.user.email}
        foto={sessao?.user.image ?? undefined}
        recolhida={compacta}
        onSair={() => void sair()}
      />
    </RodapeDaBarra>
  );

  /*
   * No celular a barra vira gaveta.
   *
   * Recolhida ela ainda ocupa 72px, que numa tela de 390px é um quinto da
   * largura gasto com uma fila de ícones - e o que sobra espreme tabela e
   * cartão. É o mesmo desenho do app do aluno, e pelo mesmo motivo.
   */
  if (!telas.md) {
    return (
      <Layout style={{ minHeight: "100dvh" }}>
        <Layout.Header
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            paddingInline: 12,
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorSplit}`,
          }}
        >
          <Button
            type="text"
            aria-label="Abrir menu"
            icon={<MenuOutlined />}
            onClick={() => setGavetaAberta(true)}
          />
          <Typography.Text strong style={{ fontSize: 17 }}>
            Titularis · Admin
          </Typography.Text>
        </Layout.Header>

        <Drawer
          placement="left"
          size={260}
          open={gavetaAberta}
          onClose={() => setGavetaAberta(false)}
          title="Titularis · Admin"
          styles={{ body: { padding: 0, display: "flex", flexDirection: "column" } }}
        >
          <div style={{ flex: 1, overflowY: "auto" }}>{menu(false)}</div>
          {perfil(false)}
        </Drawer>

        <Layout.Content style={{ padding: 16 }}>
          <Outlet />
        </Layout.Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: "100dvh" }}>
      <Layout.Sider
        theme="light"
        width={LARGURA}
        collapsedWidth={LARGURA_RECOLHIDA}
        collapsible
        collapsed={recolhida}
        onCollapse={(valor, tipo) =>
          tipo === "responsive" ? recolherPorTela(valor) : escolher(valor)
        }
        // Em tela estreita a barra recolhe sozinha, mas continua acessível pelos
        // ícones: sumir por completo esconderia a navegação inteira.
        breakpoint="lg"
        trigger={null}
        style={{
          background: token.colorBgContainer,
          borderInlineEnd: `1px solid ${token.colorSplit}`,
          // Fixa: listas longas de questões e provas não podem levar a
          // navegação embora ao rolar.
          position: "fixed",
          insetInlineStart: 0,
          top: 0,
          bottom: 0,
          height: "100dvh",
        }}
      >
        {/*
          O Sider insere um div próprio entre ele e os filhos, então o flex
          precisa ficar aqui dentro: no elemento de fora, o menu não estica e o
          perfil fica boiando no meio da barra.
        */}
        <Flex vertical style={{ height: "100%" }}>
          {/*
            A mesma marca do app do aluno, com "Admin" ao lado: é o mesmo
            produto visto por dentro, e não outro. Recolhida, sobra o símbolo -
            o sufixo cairia sozinho numa coluna de 72px.
          */}
          <CabecalhoDaBarra
            recolhida={recolhida}
            aoAlternar={escolher}
            sufixo={
              <Typography.Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                Admin
              </Typography.Text>
            }
          />

          {/* O menu rola dentro da barra se crescer, sem levar a marca junto. */}
          {menu(recolhida)}
          {perfil(recolhida)}
        </Flex>
      </Layout.Sider>

      <Layout
        style={{
          // Acompanha a barra fixa, que saiu do fluxo do documento. No celular
          // não há barra: a margem some e a tela inteira é do conteúdo.
          marginInlineStart: telas.md ? (recolhida ? LARGURA_RECOLHIDA : LARGURA) : 0,
          transition: "margin-inline-start 0.2s",
        }}
      >
        <Layout.Content style={{ padding: telas.md ? 24 : 16 }}>
          <Outlet />
        </Layout.Content>
      </Layout>
    </Layout>
  );
}
